import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  useMediaQuery,
  Container,
  Divider,
} from '@mui/material';
import SVG from 'react-inlinesvg';
import { FloorPlans, FloorPlan } from '../ProjectInterface';
import { masterlayoutbg } from '../../../../../assets';

const CLICK_HIGHLIGHT_COLOR = '#0f63a5';
const HOVER_HIGHLIGHT_COLOR = '#eaa95e';
const DEFAULT_FILL = 'transparent';
const DEFAULT_STROKE = '#000';

interface FloorPlansComponentProps {
  floorplans: FloorPlans;
}
interface Tab1Item {
  label: string; // e.g. "2BHK - 1426 sq.ft"
  bhk: number;
  sft: number;
}

const CDN_URL =
  process.env.NEXT_PUBLIC_STORAGE_DN_URL || 'https://dprstorage.b-cdn.net';

const resolveImageUrl = (path: string) => {
  if (!path) return '';
  return path.startsWith('http') ? path : `${CDN_URL}${path}`;
};

const FloorPlansNew: React.FC<FloorPlansComponentProps> = ({ floorplans }) => {
  const isMobile = useMediaQuery('(max-width:768px)');

  // Generate unique floorplan types for Tab1
  const tab1Items: Tab1Item[] = useMemo(() => {
    const uniqueTypes = new Map<string, Tab1Item>();
    floorplans.data.forEach((fp) => {
      const key = `${fp.bhk}-${fp.sft}`;
      if (!uniqueTypes.has(key)) {
        uniqueTypes.set(key, {
          label: `${fp.bhk}BHK - ${fp.sft} sq.ft`,
          bhk: fp.bhk,
          sft: fp.sft,
        });
      }
    });
    // Sort by sqft ascending
    return Array.from(uniqueTypes.values()).sort((a, b) => a.sft - b.sft);
  }, []);

  const [tab1Index, setTab1Index] = useState(0);
  const currentTab1 = tab1Items[tab1Index];

  // Helper: Get all unique facings for a BHK + SQFT
  const getFacingsFor = useCallback((bhk: number, sft: number): string[] => {
    const set = new Set<string>();
    floorplans.data.forEach((fp) => {
      if (fp.bhk === bhk && fp.sft === sft) {
        set.add(fp.facing);
      }
    });
    return Array.from(set).sort();
  }, []);

  // Get available facings for selected tab1 type for Tab2
  const tab2Facings = useMemo(() => {
    if (!currentTab1) return [];
    return getFacingsFor(currentTab1.bhk, currentTab1.sft);
  }, [currentTab1, getFacingsFor]);

  const [tab2Index, setTab2Index] = useState(0);
  const currentFacing = tab2Facings[tab2Index] || '';

  // Ensure tab2Index is always valid if facings change
  useEffect(() => {
    if (tab2Index >= tab2Facings.length) {
      setTab2Index(0);
    }
  }, [tab2Facings, tab2Index]);

  // Get IDs to highlight = all floorplans matching tab1 + tab2
  const highlightedIds = useMemo(() => {
    return floorplans.data
      .filter(
        (fp) =>
          fp.bhk === currentTab1.bhk &&
          fp.sft === currentTab1.sft &&
          fp.facing === currentFacing,
      )
      .map((fp) => fp.gid)
      .filter((gid) => !!gid); // remove empty gid
  }, [floorplans, currentTab1, currentFacing]);

  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const svgRef = useRef<HTMLDivElement | null>(null);

  // When user clicks on a <g> in SVG, find matching floorplan and set tabs accordingly
  const onGroupClick = useCallback(
    (id: string) => {
      const plan = floorplans.data.find((fp) => fp.gid === id);
      if (!plan) return;

      // Find the BHK+SQFT tab index
      const idx1 = tab1Items.findIndex(
        (item) => item.bhk === plan.bhk && item.sft === plan.sft,
      );

      if (idx1 !== -1) {
        setTab1Index(idx1);

        // Immediately get facings for this type
        const facings = getFacingsFor(plan.bhk, plan.sft);
        const idx2 = facings.indexOf(plan.facing);

        // Use a microtask to ensure the correct sequence
        Promise.resolve().then(() => setTab2Index(idx2 !== -1 ? idx2 : 0));
      }
    },
    [tab1Items, getFacingsFor],
  );

  // Update SVG styles for highlight and hover
  const updateSVGStyles = useCallback(() => {
    const svg = svgRef.current?.querySelector('svg');
    if (!svg) return;

    svg.querySelectorAll('g').forEach((g) => {
      if (!g.id) return;

      const isSelected = highlightedIds.includes(g.id);
      const isHovered = hoveredId === g.id;

      g.querySelectorAll('rect, path, polygon, ellipse').forEach((shape) => {
        shape.setAttribute(
          'fill',
          isSelected
            ? CLICK_HIGHLIGHT_COLOR
            : isHovered
            ? HOVER_HIGHLIGHT_COLOR
            : DEFAULT_FILL,
        );
        shape.setAttribute('stroke', DEFAULT_STROKE);
        shape.setAttribute(
          'stroke-width',
          isSelected ? '3' : isHovered ? '2' : '1',
        );
      });
    });
  }, [highlightedIds, hoveredId]);

  // Setup event listeners on SVG load
  const onSVGLoad = useCallback(() => {
    const svg = svgRef.current?.querySelector('svg');
    if (!svg) return;

    svg.querySelectorAll('g').forEach((g) => {
      if (!g.id) return;

      g.style.cursor = 'pointer';
      g.style.pointerEvents = 'all';

      const onMouseEnter = () => setHoveredId(g.id);
      const onMouseLeave = () => setHoveredId(null);
      const onClick = () => onGroupClick(g.id);

      g.addEventListener('mouseenter', onMouseEnter);
      g.addEventListener('mouseleave', onMouseLeave);
      g.addEventListener('click', onClick);
    });

    updateSVGStyles();
  }, [onGroupClick, updateSVGStyles]);

  // Update SVG styles when highlight or hover changes
  useEffect(() => {
    updateSVGStyles();
  }, [highlightedIds, hoveredId, updateSVGStyles]);

  // Reset tab2 index when tab1 changes
  useEffect(() => {
    setTab2Index(0);
  }, [tab1Index]);

  // Get floorplan image for current tab1 + tab2
  const floorplanImage = useMemo(() => {
    if (!currentTab1 || !currentFacing) return '';
    const fp = floorplans.data.find(
      (p) =>
        p.bhk === currentTab1.bhk &&
        p.sft === currentTab1.sft &&
        p.facing === currentFacing,
    );
    return fp?.floorplanimage || '';
  }, [currentTab1, currentFacing]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: isMobile ? 'auto' : 'auto',
        backgroundImage: isMobile
          ? `url(${masterlayoutbg})`
          : `url(${masterlayoutbg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            position: 'relative',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              mt: 3,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: '600',
                color: 'white',
                display: 'inline-block',
                mt: 4,
              }}
            >
              FLOOR PLANS
            </Typography>
            <Divider
              sx={{
                width: { xs: '350px', md: '500px' },
                height: '1px',
                backgroundColor: 'white',
                my: 2,
                borderRadius: 2,
              }}
            />
          </Box>

          {/* Outer Tabs: Floor plan types */}
          {/* Floor plan type tabs - 2 per row on mobile, horizontal scroll on desktop */}
          {isMobile ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 1,
              }}
            >
              {Array.from({ length: Math.ceil(tab1Items.length / 2) }).map(
                (_, rowIdx) => (
                  <Box
                    key={rowIdx}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      mb: 1,
                      width: '100%',
                      justifyContent: 'center',
                    }}
                  >
                    {tab1Items
                      .slice(rowIdx * 2, rowIdx * 2 + 2)
                      .map((item, i) => {
                        const idx = rowIdx * 2 + i;
                        return (
                          <Box
                            key={item.label}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              mx: 0.5,
                            }}
                          >
                            <Tab
                              label={
                                <Typography
                                  variant="h5"
                                  sx={{
                                    fontSize: 12,
                                    fontWeight:
                                      tab1Index === idx ? '600' : '400',
                                    color: 'white',
                                    textTransform: 'uppercase',
                                  }}
                                >
                                  {item.label}
                                </Typography>
                              }
                              value={idx}
                              onClick={() => setTab1Index(idx)}
                              sx={{
                                width: '100%',
                                background:
                                  tab1Index === idx ? '#14658B' : 'none',
                                color: tab1Index === idx ? 'white' : 'black',
                                fontSize: tab1Index === idx ? 16 : 14,
                                fontWeight: 'bold',
                                px: 1,
                                py: 0,
                                mb: 0,
                                border: '2px solid rgb(146, 146, 146)',
                                borderRadius: 0,
                                '&:hover': {
                                  background: '#14658B',
                                  color: 'black',
                                  border: '2px solid white',
                                },
                                transition: 'all 0.3s ease-in-out',
                                minHeight: 35,
                              }}
                            />
                          </Box>
                        );
                      })}
                  </Box>
                ),
              )}
            </Box>
          ) : (
            <Box
              sx={{
                borderColor: CLICK_HIGHLIGHT_COLOR,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Tabs
                value={tab1Index}
                onChange={(_, val) => setTab1Index(val)}
                TabIndicatorProps={{ sx: { display: 'none' } }}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="floor plan types"
                textColor="inherit"
              >
                {tab1Items.map((item, i) => (
                  <Tab
                    key={item.label}
                    label={
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: 14,
                          fontWeight: tab1Index === i ? '600' : '400',
                          color: 'white',
                          textTransform: 'uppercase',
                        }}
                      >
                        {item.label}
                      </Typography>
                    }
                    sx={{
                      background: tab1Index === i ? '#14658B' : 'none',
                      color: tab1Index === i ? 'white' : 'black',
                      fontSize: tab1Index === i ? 16 : 14,
                      fontWeight: 'bold',
                      px: 4,
                      py: 0.5,
                      mb: isMobile ? 0 : 2,
                      border: '2px solid rgb(146, 146, 146)',
                      borderRadius: 0,
                      '&:hover': {
                        background: '#14658B',
                        color: 'black',
                      },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  />
                ))}
              </Tabs>
            </Box>
          )}

          {/* Inner Tabs: Facings */}
          <Box
            sx={{
              borderColor: CLICK_HIGHLIGHT_COLOR,
              display: 'flex',
              justifyContent: 'center',
              mb: 0,
            }}
          >
            <Tabs
              value={tab2Index}
              onChange={(_, val) => setTab2Index(val)}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="floor plan facings"
              textColor="inherit"
              indicatorColor="primary"
              sx={{
                '& .MuiTabs-scrollButtons': {
                  display: 'none', // hides the arrow buttons completely
                },
              }}
            >
              {tab2Facings.map((facing, i) => (
                <Tab
                  key={facing}
                  label={
                    <Box
                      component="span"
                      sx={{
                        fontSize: tab2Index === i ? 20 : 16,
                        display: 'block',
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontSize: isMobile ? 14 : 16,
                          fontWeight: '600',
                          mr: 0.5,
                          color:
                            tab2Index === i
                              ? 'primary.main !important'
                              : '#fff',
                        }}
                      >
                        {facing}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    color: tab2Index === i ? '#c58038' : '#fff',
                    mb: 2,
                    textTransform: 'uppercase',
                    fontWeight: tab2Index === i ? 'bold' : 'normal',
                    borderBottom:
                      tab2Index === i ? '1.5px solid #0f63a5' : 'none',
                    background: tab2Index === i ? 'white' : 'none',
                    WebkitBackgroundClip: tab2Index === i ? 'text' : 'none',
                    WebkitTextFillColor:
                      tab2Index === i ? 'transparent' : 'inherit',
                    '&.Mui-selected': {
                      borderBottom: '1.5px solid #0f63a5',
                      marginBottom: -1.5,
                    },
                    '&:not(.Mui-selected)': { marginBottom: -1.5 },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Content Area */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              gap: 2,
              overflow: 'hidden',
              height: 'auto',
              flexDirection: isMobile ? 'column' : 'row',
              m: 1.5,
              p: 2,
            }}
          >
            {/* Left side floorplan image */}
            <Box
              sx={{
                flex: 1,
                borderRadius: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center5',
                maxHeight: 500,
                border: `1px solid white`,
                // backgroundColor: "white",
                boxShadow: '0 0 15px rgba(15, 99, 165)',

                m: 1.5,
                p: 4,
              }}
            >
              {floorplanImage ? (
                <img
                  src={resolveImageUrl(floorplanImage)}
                  alt={`${currentTab1.label} - ${currentFacing}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    objectPosition: 'top',
                  }}
                />
              ) : (
                <Typography>No floorplan image available</Typography>
              )}
            </Box>

            {/* Right side SVG map */}
            <Box
              sx={{
                flex: 1,
                maxHeight: isMobile ? 420 : 520,
                borderRadius: 2,
                p: 1,
                color: 'white',
              }}
              ref={svgRef}
            >
              {floorplans.layoutimage?.toLowerCase().endsWith('.png') ||
              floorplans.layoutimage?.toLowerCase().endsWith('.jpg') ||
              floorplans.layoutimage?.toLowerCase().endsWith('.jpeg') ? (
                <img
                  src={resolveImageUrl(floorplans.layoutimage)}
                  alt="Layout"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'top',
                    marginTop: '-50px',
                  }}
                />
              ) : (
                <SVG
                  src={resolveImageUrl(floorplans.layoutimage)}
                  onLoad={() => onSVGLoad()}
                  style={{ width: '100%', height: '100%' }}
                />
              )}
            </Box>
          </Box>

          {/* Info Bar */}
          {/* <Box sx={{ p: 2, bgcolor: "#111", borderTop: `1px solid ${CLICK_HIGHLIGHT_COLOR}`, textAlign: "center" }}>
          <Typography variant="body2" color={CLICK_HIGHLIGHT_COLOR}>
            Selected: {currentTab1?.label} | Facing: {currentFacing} <br />
            Highlighted IDs: {highlightedIds.join(", ") || "None"} | Hovered ID: {hoveredId ?? "None"}
          </Typography>
        </Box> */}
        </Box>
      </Container>
    </Box>
  );
};

export default FloorPlansNew;
